import { Formik, Field, Form, type FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';
import * as yup from 'yup';
import { nullTypeError } from 'utils/yup';
import { CCard, CCardBody, CCardGroup, CCol, CContainer, CRow } from '@coreui/react';
import { cilLockLocked, cilUser } from '@coreui/icons';
import ErrorMessage from 'components/common/ErrorMessage';
import { __ } from 'utils/helpers';
import Button from 'components/common/Button';
import logo from 'assets/images/logo.png';
import AuthInput from './AuthInput';

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .required(() => __('errors.email_required'))
    .email(() => __('errors.email_invalid'))
    .nonNullable(nullTypeError('string')),
  plainPassword: yup
    .string()
    .required(() => __('errors.password_required'))
    .nonNullable(nullTypeError('string')),
});

export interface ILoginFormValues {
  email: string;
  plainPassword: string;
  /** No field: the form error the submit sets with setErrors({ form }). */
  form?: string;
}

export type TLoginFormSubmit = (
  values: ILoginFormValues,
  formikActions: FormikHelpers<ILoginFormValues>,
) => Promise<void>;

interface ILoginFormProps {
  onSubmit: TLoginFormSubmit;
}

const initialValues: ILoginFormValues = {
  email: '',
  plainPassword: '',
};

export default function LoginForm({ onSubmit }: ILoginFormProps) {
  const { formatMessage: t } = useIntl();

  return (
    <Formik {...{ initialValues, validationSchema, onSubmit }}>
      {({ isSubmitting, errors }) => (
        <Form>
          <div className="auth-layout">
            <CContainer>
              <CRow className="justify-content-center">
                <CCol lg={8}>
                  <CCardGroup>
                    <CCard className="p-4">
                      <CCardBody>
                        <h1>{t({ id: 'page_titles.login' })}</h1>
                        <p className="text-muted">{t({ id: 'auth.sign_in_message' })}</p>
                        <Field
                          component={AuthInput}
                          type="email"
                          icon={cilUser}
                          name="email"
                          label={t({ id: 'auth.email' })}
                        />
                        <Field
                          component={AuthInput}
                          type="password"
                          icon={cilLockLocked}
                          name="plainPassword"
                          label={t({ id: 'auth.password' })}
                        />
                        <CRow>
                          <CCol xs={12}>
                            <Button
                              type="submit"
                              className="px-4"
                              disabled={isSubmitting}
                              loading={isSubmitting}
                            >
                              {t({ id: 'auth.sign_in_submit' })}
                            </Button>
                          </CCol>
                        </CRow>
                        <ErrorMessage message={errors.form} />
                      </CCardBody>
                    </CCard>
                    <CCard className="bg-secondary d-none d-md-flex">
                      <CCardBody className="d-flex align-items-center justify-content-center">
                        <img src={logo} alt="Logo" />
                      </CCardBody>
                    </CCard>
                  </CCardGroup>
                </CCol>
              </CRow>
            </CContainer>
          </div>
        </Form>
      )}
    </Formik>
  );
}
